import type { SetStateAction } from 'react';
import { useCallback, useState } from 'react';
import { useTransitionSync } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { FormImagePrompt, ImageUploadFile, MultiFrameTransition } from '@/app/types';
import { useImagePrompts } from './use-image-prompts';

export const useImg2VideoForm = (options?: {
  maxCount?: number;
  onImagePromptsChange?: () => void;
  allowTempMode?: boolean;
}) => {
  const { setFormParams, multiFrameTransitions } = useStore((state) => ({
    setFormParams: state.setFormParams,
    multiFrameTransitions: state.img2videoFormParams.multiFrameTransitions,
  }));
  const store = useStoreApi();

  // ========== 转场管理状态 ==========
  const [currentTransitionIndex, setCurrentTransitionIndex] = useState<number>(0);

  // ========== 工具函数 ==========
  const getImg2VideoFormParams = useCallback(() => {
    return store.getState().img2videoFormParams;
  }, [store]);

  // ========== 图片管理 ==========
  const {
    imagePrompts,
    validImageCount,
    handleUploadImage: baseHandleUploadImage,
    handleRemoveImage: baseHandleRemoveImage,
    handleImageChange,
    handleSwapImages,
    clearImages,
    setImages,
  } = useImagePrompts({
    formType: 'img2video',
    maxCount: options?.maxCount,
    onImagePromptsChange: options?.onImagePromptsChange,
    allowTempMode: options?.allowTempMode,
  });

  // ========== 转场同步逻辑 ==========
  const {
    syncTransitionsAfterImageCountChange: baseSyncTransitionsAfterImageCountChange,
    handleRemoveImageTransition: baseHandleRemoveImageTransition,
  } = useTransitionSync();

  // ========== 转场 CRUD 操作 ==========
  /**
   * 更新指定转场的时长
   */
  const updateTransitionDuration = useCallback(
    (index: number, duration: number) => {
      if (!multiFrameTransitions || !multiFrameTransitions[index]) return;

      const newTransitions = [...multiFrameTransitions];
      newTransitions[index] = {
        ...newTransitions[index],
        duration,
      };
      setFormParams('img2video', { multiFrameTransitions: newTransitions });
    },
    [multiFrameTransitions, setFormParams],
  );

  /**
   * 更新指定转场的提示词
   */
  const updateTransitionPrompt = useCallback(
    (index: number, prompt: string) => {
      if (!multiFrameTransitions || !multiFrameTransitions[index]) {
        return;
      }

      const newTransitions = [...multiFrameTransitions];
      newTransitions[index] = {
        ...newTransitions[index],
        prompt: prompt || undefined,
      };
      setFormParams('img2video', { multiFrameTransitions: newTransitions });
    },
    [multiFrameTransitions, setFormParams],
  );

  /**
   * 处理删除图片时的转场调整（包装转场同步 hook 的方法，添加额外的索引调整逻辑）
   */
  const handleRemoveImageTransition = useCallback(
    (currentTransitions: MultiFrameTransition[], removedIndex: number) => {
      // 使用转场同步 hook 的核心逻辑，然后添加额外的索引调整
      baseHandleRemoveImageTransition(currentTransitions, removedIndex, () => {
        // 调整当前选中的转场索引：避免选中已经删掉的转场
        const formParams = getImg2VideoFormParams();
        const adjusted = formParams.multiFrameTransitions || [];
        const newLength = adjusted.length;
        if (newLength === 0) {
          setCurrentTransitionIndex(0);
        } else if (currentTransitionIndex >= newLength) {
          setCurrentTransitionIndex(newLength - 1);
        } else if (newLength === 1) {
          setCurrentTransitionIndex(0);
        }
      });
    },
    [baseHandleRemoveImageTransition, getImg2VideoFormParams, setCurrentTransitionIndex, currentTransitionIndex],
  );

  /**
   * 根据有效图片数量，同步 multiFrameTransitions
   * 包装转场同步 hook 的方法，添加删除图片时的额外处理逻辑
   *
   * @param newValidImageCount 新的有效图片数量
   * @param removedIndex 可选，被删除的图片索引（当删除图片时传入）
   */
  const syncTransitionsAfterImageCountChange = useCallback(
    (newValidImageCount: number, removedIndex?: number) => {
      if (removedIndex !== undefined) {
        const formParams = getImg2VideoFormParams();
        const currentTransitions = formParams.multiFrameTransitions || [];
        handleRemoveImageTransition(currentTransitions, removedIndex);
        return;
      }

      // 其他情况直接使用转场同步 hook 的方法
      baseSyncTransitionsAfterImageCountChange(newValidImageCount);
    },
    [getImg2VideoFormParams, handleRemoveImageTransition, baseSyncTransitionsAfterImageCountChange],
  );

  /**
   * 包装上传处理：在单张上传完成时，按最新有效图片数量同步转场
   */
  const handleUploadImage = useCallback(
    (uploadFile: ImageUploadFile) => {
      baseHandleUploadImage(uploadFile);
      if (uploadFile.status === 'done') {
        const currentImagePrompts = getImg2VideoFormParams().imagePrompts.filter((p) => p !== null);
        const newValidImageCount = currentImagePrompts.length;
        syncTransitionsAfterImageCountChange(newValidImageCount);
      }
    },
    [baseHandleUploadImage, store, syncTransitionsAfterImageCountChange],
  );

  /**
   * 包装删除处理：同时同步转场删除规则
   */
  const handleRemoveImage = useCallback(
    (idx: number) => {
      const currentValid = getImg2VideoFormParams().imagePrompts.filter((p) => p !== null).length;
      const newValidImageCount = Math.max(0, currentValid - 1);
      baseHandleRemoveImage(idx);
      syncTransitionsAfterImageCountChange(newValidImageCount, idx);
    },
    [baseHandleRemoveImage, store, syncTransitionsAfterImageCountChange],
  );

  /**
   * 包装批量设置图片：设置后同步转场
   */
  const wrappedSetImages = useCallback(
    (updater: SetStateAction<FormImagePrompt[]>) => {
      setImages(updater);
      // 设置后同步转场
      setTimeout(() => {
        const currentImagePrompts = getImg2VideoFormParams().imagePrompts.filter((p) => p !== null);
        const newValidImageCount = currentImagePrompts.length;
        syncTransitionsAfterImageCountChange(newValidImageCount);
      }, 0);
    },
    [setImages, store, syncTransitionsAfterImageCountChange],
  );

  return {
    // 包含转场同步处理的图片管理
    imagePrompts,
    validImageCount,
    handleUploadImage,
    handleRemoveImage,
    handleImageChange,
    handleSwapImages,
    clearImages,
    setImages: wrappedSetImages,
    // 转场管理
    currentTransitionIndex,
    setCurrentTransitionIndex,
    multiFrameTransitions,
    updateTransitionDuration,
    updateTransitionPrompt,
    syncTransitionsAfterImageCountChange,
  };
};
